import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Phone, MessageCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import BulkUploadDialog from "@/components/BulkUploadDialog";

const KINDS = ["CUSTOMER", "BUILDER", "ARCHITECT", "DESIGNER", "CONTRACTOR"];

export default function Customers() {
  const [tab, setTab] = useState("ALL");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "CUSTOMER", phone: "", email: "", gst: "", address: "", credit_limit: 0 });

  const load = async () => {
    const params = tab === "ALL" ? {} : { kind: tab };
    const { data } = await api.get("/sales/customers", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const save = async () => {
    try { await api.post("/sales/customers", form); toast.success("Customer added"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const dial = (phone) => phone && window.open(`tel:${phone}`);
  const whatsapp = (phone) => phone && window.open(`https://wa.me/${phone.replace(/\D/g, "")}`);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="sales-customers">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Customers</h1>
          <p className="text-muted-foreground">Customers, builders, architects, interior designers and contractors — all in one rolodex.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="cust-create-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <div className="flex justify-end">
        <BulkUploadDialog
          title="Bulk upload customers"
          templateUrl="/sales/customers/template"
          uploadUrl="/sales/customers/bulk"
          payloadKey="customers"
          testId="cust-bulk"
          onSuccess={load}
        />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="ALL" data-testid="cust-tab-ALL">All</TabsTrigger>
          {KINDS.map((k) => <TabsTrigger key={k} value={k} data-testid={`cust-tab-${k}`}>{k}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Kind</TableHead><TableHead>Phone</TableHead><TableHead>Email</TableHead><TableHead>Credit limit</TableHead><TableHead>Outstanding</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`cust-row-${c.id}`}>
                <TableCell className="font-semibold">{c.name}<div className="text-[10px] text-muted-foreground">{c.address}</div></TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{c.kind}</Badge></TableCell>
                <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                <TableCell className="text-sm">{c.email || "—"}</TableCell>
                <TableCell>₹{(c.credit_limit || 0).toLocaleString()}</TableCell>
                <TableCell>₹{(c.outstanding || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => dial(c.phone)} title="Call"><Phone className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => whatsapp(c.phone)} title="WhatsApp"><MessageCircle className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No customers yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></div>
            <div><Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="cust-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>GST</Label><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Credit limit (₹)</Label><Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value || "0") })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="cust-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
